import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Link } from "react-router-dom";

const TokenDashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    setLoading(true);
    fetch("http://localhost:10002/api/token-usage")
      .then((res) => res.json())
      .then((data) => {
        // Data comes sorted by newest first from backend
        // For chart, we want oldest first (chronological)
        const chartData = [...data].reverse().map((item) => ({
          ...item,
          time: new Date(item.timestamp).toLocaleTimeString(),
          fullTime: new Date(item.timestamp).toLocaleString(),
        }));
        setData(chartData);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch token usage", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading)
    return (
      <div
        style={{
          color: "white",
          padding: 20,
          backgroundColor: "#1a1a1a",
          minHeight: "100vh",
        }}
      >
        Loading token data...
      </div>
    );

  return (
    <div
      style={{
        padding: "20px",
        color: "white",
        backgroundColor: "#1a1a1a",
        minHeight: "100vh",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h1>Token Usage Dashboard</h1>
        <div>
          <button
            onClick={fetchData}
            style={{
              marginRight: "10px",
              background: "#444",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: 5,
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
          <Link
            to="/"
            style={{
              color: "white",
              textDecoration: "none",
              background: "#333",
              padding: "10px 20px",
              borderRadius: 5,
            }}
          >
            Back to Home
          </Link>
        </div>
      </div>

      <div
        style={{
          height: 400,
          marginBottom: 40,
          background: "#222",
          padding: 20,
          borderRadius: 10,
        }}
      >
        <h3>Token Consumption Over Time</h3>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="time" stroke="#ccc" />
            <YAxis stroke="#ccc" />
            <Tooltip
              contentStyle={{
                backgroundColor: "#333",
                border: "1px solid #555",
                color: "#fff",
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="total_tokens"
              stroke="#8884d8"
              activeDot={{ r: 8 }}
              name="Total Tokens"
            />
            <Line
              type="monotone"
              dataKey="prompt_tokens"
              stroke="#82ca9d"
              name="Prompt Tokens"
            />
            <Line
              type="monotone"
              dataKey="completion_tokens"
              stroke="#ffc658"
              name="Completion Tokens"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={{ marginTop: 20 }}>
        <h3>Recent Logs</h3>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              minWidth: 800,
            }}
          >
            <thead>
              <tr
                style={{
                  borderBottom: "2px solid #555",
                  backgroundColor: "#2a2a2a",
                }}
              >
                <th style={{ padding: 15 }}>Time</th>
                <th style={{ padding: 15 }}>Model</th>
                <th style={{ padding: 15, width: "40%" }}>Prompt Content</th>
                <th style={{ padding: 15 }}>Prompt Tokens</th>
                <th style={{ padding: 15 }}>Completion Tokens</th>
                <th style={{ padding: 15 }}>Total Tokens</th>
              </tr>
            </thead>
            <tbody>
              {[...data].reverse().map((row, idx) => (
                <LogRow key={idx} row={row} idx={idx} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const LogRow = ({ row, idx }) => {
  const [expanded, setExpanded] = useState(false);

  // Truncate function
  const truncate = (str, n) => {
    if (!str) return "N/A";
    return str.length > n ? str.substr(0, n - 1) + "..." : str;
  };

  return (
    <tr
      style={{
        borderBottom: "1px solid #333",
        backgroundColor: idx % 2 === 0 ? "#1f1f1f" : "#252525",
      }}
    >
      <td style={{ padding: 15, verticalAlign: "top" }}>{row.fullTime}</td>
      <td style={{ padding: 15, verticalAlign: "top" }}>{row.model}</td>
      <td style={{ padding: 15, verticalAlign: "top" }}>
        {row.prompt_content ? (
          <div>
            <div
              style={{
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
                fontSize: "12px",
                color: "#ddd",
              }}
            >
              {expanded
                ? row.prompt_content
                : truncate(row.prompt_content, 100)}
            </div>
            {row.prompt_content.length > 100 && (
              <button
                onClick={() => setExpanded(!expanded)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#8884d8",
                  cursor: "pointer",
                  marginTop: "5px",
                  padding: 0,
                  fontSize: "12px",
                }}
              >
                {expanded ? "Show Less" : "Show More"}
              </button>
            )}
          </div>
        ) : (
          <span style={{ color: "#666" }}>No content logged</span>
        )}
      </td>
      <td style={{ padding: 15, verticalAlign: "top" }}>{row.prompt_tokens}</td>
      <td style={{ padding: 15, verticalAlign: "top" }}>
        {row.completion_tokens}
      </td>
      <td style={{ padding: 15, verticalAlign: "top" }}>{row.total_tokens}</td>
    </tr>
  );
};

export default TokenDashboard;
